import { createFileRoute } from "@tanstack/react-router";
import { Bullets, LegalPage, Note, Section, Value } from "@/components/legal-page";
import { LEGAL } from "@/lib/legal";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Xamseen — Terms of Service" },
      {
        name: "description",
        content:
          "The rules for using Xamseen: your account, the content you post, acceptable use, moderation and appeals, and the limits of the service.",
      },
      { property: "og:title", content: "Xamseen — Terms of Service" },
      { property: "og:description", content: "Your account, your content, acceptable use, moderation and appeals." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/terms" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/terms" }],
  }),
  component: TermsPage,
});

const TOC = [
  { id: "agreement", label: "These terms" },
  { id: "account", label: "Your account" },
  { id: "content", label: "Your content and who owns it" },
  { id: "acceptable", label: "Acceptable use" },
  { id: "reports", label: "Reports, accuracy and responsibility" },
  { id: "moderation", label: "Moderation, enforcement and appeals" },
  { id: "ip", label: "Xamseen's brand and platform" },
  { id: "third-party", label: "Third-party services and links" },
  { id: "disclaimer", label: "No warranty; not an emergency service" },
  { id: "liability", label: "Limitation of liability" },
  { id: "termination", label: "Ending your use of Xamseen" },
  { id: "law", label: "Governing law and disputes" },
  { id: "changes", label: "Changes to these terms" },
];

function TermsPage() {
  return (
    <LegalPage
      title="Terms of Service"
      intro="These terms are the agreement between you and Xamseen. They cover what you can expect from us, what we expect from you, and what happens when something goes wrong. They are written to be readable, not to hide anything in dense clauses."
      toc={TOC}
    >
      <Section id="agreement" title="1. These terms">
        <p>
          Xamseen is operated by <Value value={LEGAL.legalEntity} label="registered legal entity name" />. By creating an
          account or using Xamseen, you agree to these terms and to our{" "}
          <a className="text-plum hover:underline" href="/community-guidelines">
            Community Guidelines
          </a>
          , and you acknowledge our{" "}
          <a className="text-plum hover:underline" href="/privacy">
            Privacy Policy
          </a>
          . If you do not agree, please do not use Xamseen.
        </p>
      </Section>

      <Section id="account" title="2. Your account">
        <Bullets
          items={[
            <>
              You must be at least {LEGAL.minimumAge} years old, or older if your country requires a higher age for this
              kind of service.
            </>,
            <>Give accurate account information and keep your email address current so we can reach you about security.</>,
            <>
              You are responsible for activity under your account and for keeping your credentials private. Tell us
              promptly if you think your account has been compromised.
            </>,
            <>
              One person, one account, unless we have agreed otherwise. Accounts that impersonate a person,
              organisation, official or authority are not permitted.
            </>,
            <>
              Verified or official account labels are granted by Xamseen and may be removed if the basis for them no
              longer holds.
            </>,
          ]}
        />
      </Section>

      <Section id="content" title="3. Your content and who owns it">
        <p>
          <strong>You keep ownership of what you create.</strong> Your reports, photos, videos, posts, answers and
          comments remain yours.
        </p>
        <p>
          To run Xamseen we need permission to use that content in the ways the product obviously requires. You grant us
          a worldwide, non-exclusive, royalty-free licence to host, store, reproduce, adapt for display (for example
          resizing images), publish, and distribute your content within Xamseen, and to include it in aggregated or
          de-identified civic insight derived from many reports. This licence exists so the service can function; it does
          not let us sell your content as a standalone product.
        </p>
        <p>
          Only post content you have the right to post. Do not upload other people's photos, video or writing without
          permission.
        </p>
        <p>
          When you delete content, we stop displaying it and remove it from active systems. Where content has become part
          of a shared community record of an issue, a de-identified version may remain, and copies may persist briefly in
          backups.
        </p>
      </Section>

      <Section id="acceptable" title="4. Acceptable use">
        <p>Do not use Xamseen to:</p>
        <Bullets
          items={[
            <>Harass, threaten, bully or incite violence or hatred against people or groups.</>,
            <>Post content that is unlawful, defamatory, or sexually exploitative, especially involving minors.</>,
            <>Submit deliberately false, fabricated or misleading reports, or manipulate confirmations and votes.</>,
            <>Impersonate anyone, or falsely present yourself as an official, authority or verified organisation.</>,
            <>
              Publish other people's private information — home addresses, phone numbers, identity documents, medical
              information — without their consent.
            </>,
            <>Post spam, scams, phishing, malware or coordinated inauthentic activity.</>,
            <>
              Scrape, bulk-extract, resell or redistribute Xamseen data, or use automated access that we have not
              authorised.
            </>,
            <>Interfere with the security or integrity of the platform, or attempt to bypass access controls.</>,
            <>Use Xamseen to conduct surveillance of individuals or to target a person for harm.</>,
          ]}
        />
      </Section>

      <Section id="reports" title="5. Reports, accuracy and responsibility">
        <p>
          Reports on Xamseen are community observations. They are not verified facts, official determinations, or legal
          findings, and community confirmation is a signal of local agreement rather than proof.
        </p>
        <p>
          Automated assessment may suggest a category, a severity level or a likely responsible authority for a report.
          These are suggestions to help route and prioritise the issue. They can be wrong, they are not professional,
          engineering, legal or safety advice, and they are not decisions made by any authority.
        </p>
        <p>
          Xamseen does not guarantee that any report will be seen, acknowledged, actioned or resolved by any authority or
          organisation. Whether a problem gets fixed depends on the people and institutions responsible for fixing it.
        </p>
      </Section>

      <Section id="moderation" title="6. Moderation, enforcement and appeals">
        <p>
          We may remove content, limit its distribution, restrict features, or suspend or terminate accounts that breach
          these terms or the Community Guidelines. Where it is reasonable and lawful, we aim to tell you what happened
          and why.
        </p>
        <p>
          If you believe a decision was wrong, you can appeal it by contacting us. Appeals are reviewed by a person, and
          we will restore content or access if the decision was mistaken. Our moderation is described in more detail in
          the{" "}
          <a className="text-plum hover:underline" href="/community-guidelines">
            Community Guidelines
          </a>
          .
        </p>
      </Section>

      <Section id="ip" title="7. Xamseen's brand and platform">
        <p>
          The Xamseen name, logo, wordmark, design and software are ours. These terms do not grant you a licence to use
          our brand, and you may not copy, resell or create a substitute service from Xamseen's software or data.
        </p>
      </Section>

      <Section id="third-party" title="8. Third-party services and links">
        <p>
          Xamseen relies on third-party services for hosting, maps, email and automated assessment, and community content
          may link to other websites. We do not control third-party services or the content they host, and we are not
          responsible for them. Their own terms and privacy policies apply to your use of them.
        </p>
      </Section>

      <Section id="disclaimer" title="9. No warranty; not an emergency service">
        <Note tone="warn">
          Xamseen is not an emergency service and must never be used to report an emergency. If there is a risk to life,
          health, safety or property, contact your local emergency number or the responsible authority directly.
        </Note>
        <p>
          Xamseen is provided "as is" and "as available". To the fullest extent permitted by law, we disclaim implied
          warranties of merchantability, fitness for a particular purpose and non-infringement. We do not warrant that
          the service will be uninterrupted, error-free, or that content on it is accurate or complete.
        </p>
      </Section>

      <Section id="liability" title="10. Limitation of liability">
        <p>
          To the fullest extent permitted by law, Xamseen and its operators are not liable for indirect, incidental,
          special, consequential or punitive damages, or for lost profits, data or goodwill, arising from your use of the
          service. Nothing in these terms excludes liability that cannot lawfully be excluded, and some jurisdictions do
          not allow certain exclusions, so parts of this section may not apply to you.
        </p>
        <p>
          The monetary cap on our liability is <Value value={null} label="liability cap, to be set with legal counsel" />.
        </p>
      </Section>

      <Section id="termination" title="11. Ending your use of Xamseen">
        <p>
          You can delete your account at any time in Settings → Privacy &amp; your data. We may suspend or terminate
          access if you breach these terms, if required by law, or if continuing would create risk for other people or
          for the service. Sections that by their nature should survive termination — content licences already granted,
          disclaimers, liability limits and dispute terms — continue to apply.
        </p>
      </Section>

      <Section id="law" title="12. Governing law and disputes">
        <p>
          These terms are governed by{" "}
          <Value value={LEGAL.governingLaw} label="governing law and competent courts" />, without prejudice to any
          mandatory consumer protection rights you have in your country of residence.
        </p>
        <p>
          Please contact us first — most disputes are resolved faster that way. Reach us at{" "}
          <Value value={LEGAL.supportEmail} label="general support and legal contact address" />.
        </p>
      </Section>

      <Section id="changes" title="13. Changes to these terms">
        <p>
          We may update these terms as Xamseen develops. We will update the date at the top and, for material changes,
          notify you in the product or by email before they take effect. Continuing to use Xamseen after a change means
          you accept the updated terms.
        </p>
      </Section>
    </LegalPage>
  );
}
